import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Button,
  DialogActions,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { Ref, useState } from "react";
import { $Service, Service } from "../_generated";
import { useForm } from "react-hook-form";
import { validationFromSchema } from "../util";
import { TabbedRepositorySelector } from "../components/TabbedRepositorySelector";
export type UserRepository = {
  name: string;
};

export type UserAppInstallation = {
  id: number;
};

export default function ServiceInitDialog(props: {
  open: boolean;
  service?: Service;
  onCancel: () => any;
  onSubmit: (data: Service) => any;
  onChooseDatabase: () => any;
  projectName: string;
}) {
  const [serviceType, setServiceType] = useState("");
  const [selectedRepoInfo, setSelectedRepoInfo] = useState(
    {} as { name: string; type: string }
  );
  const { register, handleSubmit, errors } = useForm();

  let dialogContent;
  if (serviceType === "") {
    dialogContent = (
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              Application services (your code)
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={2} direction="row">
          <Grid item xs={6}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => setServiceType("container")}
            >
              Backend (container)
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => setServiceType("webapp")}
            >
              Static web app
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Resources</Typography>
          </Grid>
        </Grid>
        <Grid container spacing={2} direction="row">
          <Grid item xs={6}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => props.onChooseDatabase()}
            >
              Postgres database
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button variant="outlined" color="primary" fullWidth disabled>
              DynamoDB
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button variant="outlined" color="primary" fullWidth disabled>
              Redis
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button variant="outlined" color="primary" fullWidth disabled>
              ElasticSearch
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    );
  } else {
    dialogContent = (
      <DialogContent>
        <Grid container spacing={2} direction="column" alignItems="flex-start">
          <Grid item xs={12}>
            <TabbedRepositorySelector
              selectedRepoInfo={selectedRepoInfo}
              projectName={props.projectName}
              onChange={setSelectedRepoInfo}
            />
          </Grid>
          <Grid item xs={12}>
            {serviceType === "container" && (
              <ContainerFields register={register} errors={errors} />
            )}
            {serviceType === "webapp" && (
              <WebappFields register={register} errors={errors} />
            )}
          </Grid>
        </Grid>
      </DialogContent>
    );
  }

  return (
    <Dialog open={props.open} fullWidth onEnter={() => setServiceType("")}>
      <form
        onSubmit={handleSubmit((data: any) => {
          if (!selectedRepoInfo.name || !selectedRepoInfo.type) {
            alert("please select a repository!");
            return;
          }
          return props.onSubmit({
            ...props.service,
            ...data,
            service_type: serviceType,
            repository: selectedRepoInfo.name,
            repository_type: selectedRepoInfo.type,
          });
        })}
      >
        <DialogTitle>New Service</DialogTitle>
        {dialogContent}
        <DialogActions>
          <Button onClick={props.onCancel}>Cancel</Button>
          {serviceType && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disableElevation
            >
              Create
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

//TODO refactor into a separate dialog instead with common parts extracted into a component
function ContainerFields({
  register,
  errors,
}: {
  register: (r: any) => Ref<any>;
  errors: any;
}) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register(validationFromSchema($Service.properties.name))}
          label="Name"
          name="alias"
          required
          error={errors.name !== undefined}
          helperText={errors.name?.message}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register}
          label="Service path"
          name="path"
          required
          defaultValue="./"
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register(
            validationFromSchema($Service.properties.dockerfile)
          )}
          label="Dockerfile"
          name="dockerfile"
          required
          defaultValue="./Dockerfile"
          error={errors.dockerfile !== undefined}
          helperText={errors.dockerfile?.message}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register(
            validationFromSchema($Service.properties.health_check)
          )}
          label="Health check"
          name="health_check"
          required
          defaultValue="/"
          error={errors.health_check !== undefined}
          helperText={errors.health_check?.message}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register(
            validationFromSchema($Service.properties.container_port)
          )}
          label="Container port"
          name="container_port"
          required
          defaultValue="8080"
          error={errors.container_port !== undefined}
          helperText={errors.container_port?.message}
          fullWidth
        />
      </Grid>
    </Grid>
  );
}

//TODO refactor into a separate dialog instead with common parts extracted into a component
function WebappFields({
  register,
  errors,
}: {
  register: (r: any) => Ref<any>;
  errors: any;
}) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register(validationFromSchema($Service.properties.name))}
          label="Name"
          name="alias"
          required
          error={errors.name !== undefined}
          helperText={errors.name?.message}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register}
          label="Service path"
          name="service_path"
          defaultValue="./app"
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register}
          label="Build directory"
          name="build_directory"
          defaultValue="./app/dist"
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          inputRef={register}
          label="Build Command"
          name="build_command"
          defaultValue="npm run build"
          fullWidth
        />
      </Grid>
    </Grid>
  );
}
